import { useState, useEffect, useRef } from 'react';
import { Send, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PostCard from '../components/PostCard';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(true); // Simulated unread state
    const navigate = useNavigate();
    const feedRef = useRef(null);

    const fetchPosts = async () => {
        try {
            const response = await axios.get(`${API}/posts`);
            setPosts(response.data);
        } catch (error) {
            console.error('Error fetching posts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();

        // Listen for new posts
        const handlePostCreated = () => {
            fetchPosts();
        };
        window.addEventListener('post-created', handlePostCreated);
        return () => window.removeEventListener('post-created', handlePostCreated);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-black" data-testid="home-page">
            {/* Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto flex items-center justify-between px-4 h-14">
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        OneGame
                    </h1>
                    {/* Messages Button with Notification Dot */}
                    <button
                        onClick={() => navigate('/messages')}
                        className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="messages-button"
                    >
                        <Send className="w-5 h-5 text-white" />
                        {/* Notification Dot */}
                        {hasUnreadMessages && (
                            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-primary rounded-full border-2 border-black" />
                        )}
                    </button>
                </div>
            </div>

            {/* Feed - with bottom padding for nav bar */}
            <div 
                ref={feedRef}
                className="flex-1 overflow-y-auto snap-feed hide-scrollbar pb-20"
                style={{ height: 'calc(100dvh - 3.5rem)' }}
                data-testid="posts-feed"
            >
                {posts.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-white/60">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-center"
                        >
                            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mx-auto mb-4">
                                <RefreshCw className="w-8 h-8 text-white/40" />
                            </div>
                            <p className="text-lg font-medium">No posts yet</p>
                            <p className="text-sm mt-1">Be the first to share!</p>
                        </motion.div>
                    </div>
                ) : (
                    posts.map((post, index) => (
                        <PostCard 
                            key={post.id} 
                            post={post}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export default HomePage;
